import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import fileDownload from 'js-file-download';
import FileService from '../../../../services/FileService';
import { rotuloPrioridade } from './kmzFicha';

function corPrioridade(valor) {
  const texto = String(valor || '').trim().toUpperCase();
  if (texto === 'E' || texto === '1') return { bgcolor: '#fef2f2', color: '#b91c1c' };
  if (texto === '2') return { bgcolor: '#fff7ed', color: '#c2410c' };
  return { bgcolor: 'action.hover', color: 'text.primary' };
}

function extensao(tipo) {
  if (tipo === 'image/png') return 'png';
  if (tipo === 'image/webp') return 'webp';
  return 'jpg';
}

function nomeArquivo(titulo, indice, total, tipo) {
  const base = String(titulo || 'foto').replace(/[<>:"/\\|?*]/g, ' ').replace(/\s+/g, ' ').trim() || 'foto';
  const ext = extensao(tipo);
  if (total <= 1) return `${base}.${ext}`;
  return `${base}-${indice + 1}.${ext}`;
}

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 5;
const ZOOM_PASSO = 0.25;

const transformacaoInicial = () => ({ scale: 1, rotate: 0, x: 0, y: 0 });

function limitarZoom(valor) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(valor.toFixed(2))));
}

function BotaoFoto({ title, onClick, disabled, children }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          aria-label={title}
          onClick={onClick}
          disabled={disabled}
          size="small"
          sx={{
            color: 'common.white',
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.12),
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.white, 0.22) },
            '&.Mui-disabled': { color: (theme) => alpha(theme.palette.common.white, 0.28) },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

BotaoFoto.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const KmzFotoPainel = ({ ficha, indice, onIndice, onClose }) => {
  const fotos = (ficha && ficha.fotos) || [];
  const atual = fotos[indice] || fotos[0] || '';
  const varias = fotos.length > 1;
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState('');
  const [transformacao, setTransformacao] = useState(transformacaoInicial);
  const arraste = useRef(null);
  const palco = useRef(null);
  const painel = useRef(null);

  useEffect(() => {
    setTransformacao(transformacaoInicial());
    arraste.current = null;
  }, [indice, atual]);

  useEffect(() => {
    const el = painel.current;
    if (!el) return undefined;
    const segurar = (event) => event.stopPropagation();
    el.addEventListener('wheel', segurar);
    return () => el.removeEventListener('wheel', segurar);
  }, []);

  useEffect(() => {
    const el = palco.current;
    if (!el) return undefined;
    const naRoda = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const delta = event.deltaY > 0 ? -ZOOM_PASSO : ZOOM_PASSO;
      setTransformacao((prev) => {
        const scale = limitarZoom(prev.scale + delta);
        if (scale <= 1) return { ...prev, scale, x: 0, y: 0 };
        return { ...prev, scale };
      });
    };
    el.addEventListener('wheel', naRoda, { passive: false });
    return () => el.removeEventListener('wheel', naRoda);
  }, [atual]);

  const zoomPor = (delta) => {
    setTransformacao((prev) => {
      const scale = limitarZoom(prev.scale + delta);
      if (scale <= 1) return { ...prev, scale, x: 0, y: 0 };
      return { ...prev, scale };
    });
  };

  const ajustar = () => {
    arraste.current = null;
    setTransformacao(transformacaoInicial());
  };

  const girar = (graus) => {
    setTransformacao((prev) => ({ ...prev, rotate: prev.rotate + graus }));
  };

  const aoPressionar = (event) => {
    if (transformacao.scale <= 1) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    arraste.current = {
      x: event.clientX,
      y: event.clientY,
      ox: transformacao.x,
      oy: transformacao.y,
    };
  };

  const aoMover = (event) => {
    if (!arraste.current) return;
    const origem = arraste.current;
    setTransformacao((prev) => ({
      ...prev,
      x: origem.ox + (event.clientX - origem.x),
      y: origem.oy + (event.clientY - origem.y),
    }));
  };

  const aoSoltar = () => {
    arraste.current = null;
  };

  const ir = (passo) => {
    if (!varias) return;
    const proximo = (indice + passo + fotos.length) % fotos.length;
    setErro('');
    onIndice(proximo);
  };

  const baixar = async () => {
    if (!atual || baixando) return;
    setBaixando(true);
    setErro('');
    try {
      const response = await FileService.getFile(atual);
      const blob = response && response.data;
      fileDownload(blob, nomeArquivo(ficha.titulo, indice, fotos.length, blob && blob.type));
    } catch (error) {
      setErro('Não foi possível baixar a foto.');
    } finally {
      setBaixando(false);
    }
  };

  return (
    <Box
      ref={painel}
      onClick={onClose}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'rgba(15, 23, 42, 0.55)',
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          maxWidth: 980,
          height: '100%',
          maxHeight: 680,
          bgcolor: 'background.paper',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 240,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#0f172a',
          }}
        >
          <Box ref={palco} sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {atual ? (
              <Box
                component="img"
                src={atual}
                alt=""
                onPointerDown={aoPressionar}
                onPointerMove={aoMover}
                onPointerUp={aoSoltar}
                onPointerCancel={aoSoltar}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  transform: `translate(${transformacao.x}px, ${transformacao.y}px) rotate(${transformacao.rotate}deg) scale(${transformacao.scale})`,
                  cursor: transformacao.scale > 1 ? 'grab' : 'default',
                  touchAction: 'none',
                }}
              />
            ) : null}
            {varias ? (
              <>
                <IconButton
                  aria-label="Foto anterior"
                  onClick={() => ir(-1)}
                  sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.35)' }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  aria-label="Próxima foto"
                  onClick={() => ir(1)}
                  sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', bgcolor: 'rgba(0,0,0,0.35)' }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            ) : null}
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                position: 'absolute',
                left: '50%',
                bottom: 12,
                transform: 'translateX(-50%)',
                px: 1,
                py: 0.75,
                borderRadius: 8,
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.55),
              }}
            >
              <BotaoFoto title="Diminuir zoom" onClick={() => zoomPor(-ZOOM_PASSO)} disabled={!atual}>
                <ZoomOutIcon fontSize="small" />
              </BotaoFoto>
              <BotaoFoto title="Aumentar zoom" onClick={() => zoomPor(ZOOM_PASSO)} disabled={!atual}>
                <ZoomInIcon fontSize="small" />
              </BotaoFoto>
              <BotaoFoto title="Ajustar à tela" onClick={ajustar} disabled={!atual}>
                <FitScreenIcon fontSize="small" />
              </BotaoFoto>
              <BotaoFoto title="Girar à esquerda" onClick={() => girar(-90)} disabled={!atual}>
                <RotateLeftIcon fontSize="small" />
              </BotaoFoto>
              <BotaoFoto title="Girar à direita" onClick={() => girar(90)} disabled={!atual}>
                <RotateRightIcon fontSize="small" />
              </BotaoFoto>
              <BotaoFoto title={baixando ? 'Baixando' : 'Baixar foto'} onClick={baixar} disabled={!atual || baixando}>
                <DownloadOutlinedIcon fontSize="small" />
              </BotaoFoto>
            </Stack>
            {erro ? (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  bottom: 56,
                  textAlign: 'center',
                  color: '#fecaca',
                }}
              >
                {erro}
              </Typography>
            ) : null}
          </Box>
          {varias ? (
            <Stack direction="row" spacing={1} sx={{ p: 1.25, overflowX: 'auto' }}>
              {fotos.map((url, fotoIndice) => (
                <Box
                  key={`${url}-${fotoIndice}`}
                  component="button"
                  type="button"
                  aria-label={`Foto ${fotoIndice + 1}`}
                  onClick={() => {
                    setErro('');
                    onIndice(fotoIndice);
                  }}
                  sx={{
                    width: 56,
                    height: 56,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: fotoIndice === indice ? 'primary.main' : 'transparent',
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
        <Stack sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, p: 2, overflow: 'auto' }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {ficha.titulo || 'Serviço'}
              </Typography>
              {varias ? (
                <Typography variant="caption" color="text.secondary">
                  {`${indice + 1} de ${fotos.length}`}
                </Typography>
              ) : null}
            </Box>
            <IconButton aria-label="Fechar" onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          {ficha.tipo ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {ficha.tipo}
            </Typography>
          ) : null}
          {(ficha.textos || []).map((texto) => (
            <Typography key={texto} variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
              {texto}
            </Typography>
          ))}
          {(ficha.digitacao || []).map((campo) => (
            <Box key={`d-${campo.rotulo}-${campo.valor}`} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{campo.rotulo}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{campo.valor}</Typography>
            </Box>
          ))}
          {(ficha.servicoCampos || []).map((campo) => (
            <Box key={`s-${campo.rotulo}-${campo.valor}`} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{campo.rotulo}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{campo.valor}</Typography>
            </Box>
          ))}
          {(ficha.servicos || []).length ? (
            <Stack spacing={0} sx={{ mt: 1 }}>
              {ficha.servicos.map((item, indice) => (
                <Stack
                  key={`${item.nome}-${item.prioridade}-${indice}`}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{
                    py: 1,
                    borderTop: indice === 0 && !(ficha.servicoCampos || []).length ? 0 : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {item.prioridade ? (
                    <Box
                      sx={{
                        flexShrink: 0,
                        mt: '1px',
                        px: 0.75,
                        borderRadius: 0.5,
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: '18px',
                        ...corPrioridade(item.prioridade),
                      }}
                    >
                      {rotuloPrioridade(item.prioridade)}
                    </Box>
                  ) : null}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                      {item.nome}
                    </Typography>
                    {item.descricao ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                        {item.descricao}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : null}
          {(ficha.poste || []).concat(ficha.campos || []).map((campo) => (
            <Box key={`${campo.rotulo}-${campo.valor}`} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{campo.rotulo}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{campo.valor}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

KmzFotoPainel.propTypes = {
  ficha: PropTypes.shape({
    titulo: PropTypes.string,
    tipo: PropTypes.string,
    textos: PropTypes.arrayOf(PropTypes.string),
    campos: PropTypes.arrayOf(PropTypes.shape({
      rotulo: PropTypes.string,
      valor: PropTypes.string,
    })),
    fotos: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  indice: PropTypes.number.isRequired,
  onIndice: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default KmzFotoPainel;
